import { CommonModule } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges
} from "@angular/core";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { NzSpinModule } from "ng-zorro-antd/spin";
import { NzPaginationModule } from "ng-zorro-antd/pagination";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzSelectModule } from "ng-zorro-antd/select";
import { FormsModule } from "@angular/forms";
import { Subject, Subscription, takeUntil } from "rxjs";
import { NavigationMenuSelection } from "../../../interfaces/navigation_menu.interface";
import {
    QuestionResponseActivityDto,
    QuestionResponseActivityService
} from "../../../services/question_response_activity.service";
import {
    QuestionResponseMutationResponse,
    QuestionResponsesService
} from "../../../services/question_responses.service";
import { ProductLicenseSummary, ProductLicensesService } from "../../../services/product_licenses.service";
import { LoginService } from "../../../services/login.service";
import { FormNavigationDispatcherService } from "../../../services/form_navigation_dispatcher.service";

interface RecentActivityEntry {
    id: number;
    description: string;
    createdAt: string;
    userDisplayName: string | null;
    userRole: string | null;
    questionReference: string | null;
}

const SECTION_PATTERN = /^[A-Za-z0-9_]+$/;
const TARGET_PATTERN = /^[A-Za-z0-9 _.-]+$/;
const DEFAULT_PAGE_SIZE = 5;

@Component({
    selector: "td[appSaqRecentActivityFooter]",
    templateUrl: "./recent_activity.component.html",
    styleUrls: ["./recent_activity.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, FormsModule, NzSpinModule, NzPaginationModule, NzButtonModule, NzIconModule, NzSelectModule]
})
export class RecentActivityFooterComponent implements OnChanges, OnDestroy {
    private static nextInstanceId = 0;

    @Input()
    sectionId = "";

    @Input()
    sectionKey = "";

    @Input()
    readOnlyUser = false;

    @Input()
    aggregateByLicense = false;

    @Input()
    productLicenseId: number | null = null;

    @HostBinding("class")
    readonly hostClass = "recent-activity";

    readonly componentId = ++RecentActivityFooterComponent.nextInstanceId;

    @HostBinding("style.display")
    protected get hostDisplay(): string | null {
        return this.canViewRecentActivity ? null : "none";
    }

    protected activities: RecentActivityEntry[] = [];
    protected filteredActivities: RecentActivityEntry[] = [];
    protected authorOptions: string[] = [];
    protected selectedAuthorNames: string[] = [];
    protected areFiltersVisible = false;
    protected isLoading = false;
    protected isExpanded = false;
    protected canViewRecentActivity = false;
    protected pageIndex = 1;
    protected readonly pageSize = DEFAULT_PAGE_SIZE;
    protected total = 0;

    private currentSelection: NavigationMenuSelection | null = null;
    private lastSelectionKey: string | null = null;
    private loadSequence = 0;
    private readonly destroy$ = new Subject<void>();
    private licenseAccessSubscription: Subscription | null = null;
    private pendingMutationRefresh = false;
    private lastContextLicenseId: number | null = null;

    constructor(
        private readonly activityService: QuestionResponseActivityService,
        private readonly questionResponsesService: QuestionResponsesService,
        private readonly productLicensesService: ProductLicensesService,
        private readonly loginService: LoginService,
        private readonly notification: NzNotificationService,
        private readonly navigationDispatcher: FormNavigationDispatcherService,
        private readonly cdr: ChangeDetectorRef
    ) {
        this.questionResponsesService
            .watchMutationEvents()
            .pipe(takeUntil(this.destroy$))
            .subscribe((mutation) => {
                if (!this.shouldRefreshForMutation(mutation)) {
                    return;
                }

                if (this.isLoading) {
                    this.pendingMutationRefresh = true;
                    return;
                }

                if (this.isRecentActivityAlreadyCurrent(mutation)) {
                    return;
                }

                this.loadRecentActivity();
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        // A reused footer starts a new requirement collapsed without resetting
        // its existing activity loading and pagination paths.
        if (changes["sectionId"] || changes["sectionKey"] || changes["aggregateByLicense"] ||
            (changes["readOnlyUser"] && this.readOnlyUser)) {
            this.isExpanded = false;
        }
        if (changes["sectionId"] || changes["sectionKey"] || changes["readOnlyUser"] ||
            changes["aggregateByLicense"] || changes["productLicenseId"]) {
            this.updateSelection(this.sectionId, this.sectionKey);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.licenseAccessSubscription?.unsubscribe();
    }

    protected trackById(_index: number, item: RecentActivityEntry): number {
        return item.id;
    }

    @HostListener("click")
    protected expandIfCollapsed(): void {
        if (!this.isExpanded) {
            this.isExpanded = true;
            this.markForCheck();
        }
    }

    protected toggleExpanded(): void {
        this.isExpanded = !this.isExpanded;
        this.markForCheck();
    }

    protected showFilters(): void {
        this.areFiltersVisible = true;
        this.markForCheck();
    }

    protected onAuthorFilterChange(values: string[]): void {
        this.selectedAuthorNames = Array.isArray(values) ? values : [];
        this.rebuildFilteredActivities();
        this.markForCheck();
    }

    protected onPageChange(pageIndex: number): void {
        if (pageIndex === this.pageIndex) {
            return;
        }

        this.pageIndex = pageIndex;
        this.loadRecentActivity();
    }

    private updateSelection(sectionId: string, sectionKey: string): void {
        if (this.readOnlyUser) {
            this.licenseAccessSubscription?.unsubscribe();
            this.licenseAccessSubscription = null;
            this.resetActivityState();
            this.canViewRecentActivity = false;
            this.currentSelection = null;
            this.lastSelectionKey = null;
            this.lastContextLicenseId = null;
            this.pageIndex = 1;
            this.markForCheck();
            return;
        }

        const normalized = this.normalizeSelection(sectionId, sectionKey);
        const reference = this.aggregateByLicense ? "__license__" : this.toQuestionReference(normalized);
        const licenseId = this.getCurrentLicenseId();

        if (reference === this.lastSelectionKey && licenseId === this.lastContextLicenseId) {
            this.currentSelection = normalized;
            this.evaluateAccessPermissions(normalized);
            return;
        }

        this.lastSelectionKey = reference;
        this.lastContextLicenseId = licenseId;
        this.currentSelection = normalized;
        this.pageIndex = 1;
        this.resetActivityState();
        this.canViewRecentActivity = false;

        if (!normalized && !this.aggregateByLicense) {
            this.markForCheck();
            return;
        }

        this.evaluateAccessPermissions(normalized);
    }

    private loadRecentActivity(): void {
        const selection = this.currentSelection;
        if (!selection && !this.aggregateByLicense) {
            this.resetActivityState();
            this.markForCheck();
            return;
        }

        const licenseId = this.getCurrentLicenseId();
        if (licenseId === null) {
            this.resetActivityState();
            this.markForCheck();
            return;
        }

        const requestId = ++this.loadSequence;

        this.isLoading = true;
        this.activities = [];
        this.rebuildFilteredActivities();
        this.markForCheck();

        this.activityService
            .listRecentActivity(selection ?? null, this.pageIndex, licenseId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    if (this.loadSequence !== requestId) {
                        return;
                    }

                    this.activities = response.items.map((item) => this.normalizeActivity(item));
                    this.rebuildFilteredActivities();
                    this.total = response.total;
                    this.isLoading = false;
                    if (this.pendingMutationRefresh) {
                        this.pendingMutationRefresh = false;
                        this.loadRecentActivity();
                        return;
                    }
                    this.markForCheck();
                },
                error: (error) => {
                    if (this.loadSequence !== requestId) {
                        return;
                    }

                    this.activities = [];
                    this.rebuildFilteredActivities();
                    this.isLoading = false;
                    this.total = 0;
                    if (this.pendingMutationRefresh) {
                        this.pendingMutationRefresh = false;
                        this.loadRecentActivity();
                        return;
                    }
                    const message =
                        error?.error?.message ||
                        error?.message ||
                        "Unable to load recent activity.";
                    this.notification.error("Load failed", message);
                    this.markForCheck();
                }
            });
    }

    private evaluateAccessPermissions(selection: NavigationMenuSelection | null): void {
        if (this.readOnlyUser) {
            this.canViewRecentActivity = false;
            this.resetActivityState();
            this.markForCheck();
            return;
        }

        const activeLicenseId = this.getCurrentLicenseId();
        this.licenseAccessSubscription?.unsubscribe();
        this.licenseAccessSubscription = null;

        if (this.loginService.hasAdminPrivileges()) {
            this.canViewRecentActivity = activeLicenseId !== null;
            if (this.canViewRecentActivity) {
                this.loadRecentActivity();
            } else {
                this.resetActivityState();
            }
            this.markForCheck();
            return;
        }

        if (activeLicenseId === null) {
            this.canViewRecentActivity = false;
            this.resetActivityState();
            this.markForCheck();
            return;
        }

        this.canViewRecentActivity = false;
        this.markForCheck();
        this.licenseAccessSubscription = this.productLicensesService
            .getLicenseById(activeLicenseId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (license) => {
                    if (activeLicenseId !== this.getCurrentLicenseId()) {
                        return;
                    }
                    this.canViewRecentActivity = this.isUserAssignedToLicense(license);
                    if (this.canViewRecentActivity) {
                        this.loadRecentActivity();
                    } else {
                        this.resetActivityState();
                    }
                    this.markForCheck();
                },
                error: () => {
                    if (activeLicenseId !== this.getCurrentLicenseId()) {
                        return;
                    }
                    this.canViewRecentActivity = false;
                    this.resetActivityState();
                    this.markForCheck();
                }
            });
    }

    private resetActivityState(): void {
        ++this.loadSequence;
        this.activities = [];
        this.selectedAuthorNames = [];
        this.areFiltersVisible = false;
        this.rebuildFilteredActivities();
        this.isLoading = false;
        this.total = 0;
        this.pendingMutationRefresh = false;
    }

    private rebuildFilteredActivities(): void {
        this.authorOptions = Array.from(new Set([
            ...this.selectedAuthorNames,
            ...this.activities.map((activity) => activity.userDisplayName)
                .filter((name): name is string => !!name)
        ])).sort((a, b) => a.localeCompare(b));

        const selectedAuthors = new Set(this.selectedAuthorNames);
        this.filteredActivities = this.activities.filter((activity) =>
            selectedAuthors.size === 0 ||
            (!!activity.userDisplayName && selectedAuthors.has(activity.userDisplayName))
        );
    }

    private isUserAssignedToLicense(license: ProductLicenseSummary | null): boolean {
        const userId = this.normalizeUserId(this.loginService.getCurrentUserId());
        if (!license || userId === null) {
            return false;
        }

        const assignmentIds = [
            license.assignedIsaUserId,
            license.assignedQsaUserId,
            license.assignedQaUserId,
            ...(license.editorIds ?? [])
        ]
            .map((id) => this.normalizeUserId(id))
            .filter((id): id is number => Number.isInteger(id));

        return assignmentIds.some((id) => id === userId);
    }


    protected onRequirementReferenceClick(reference: string | null | undefined): void {
        const selection = this.parseSelectionFromReference(reference);
        if (!selection) {
            return;
        }

        this.navigationDispatcher.requestNavigation(selection);
    }

    protected getRequirementReferenceLabel(reference: string | null | undefined): string {
        const selection = this.parseSelectionFromReference(reference);
        if (!selection) {
            return "";
        }

        return this.formatRequirementReferenceTarget(selection.target);
    }

    private normalizeLicenseId(value: number | null): number | null {
        const numeric = Number(value);
        return Number.isSafeInteger(numeric) && numeric > 0 ? numeric : null;
    }

    private getCurrentLicenseId(): number | null {
        return this.normalizeLicenseId(this.aggregateByLicense
            ? this.productLicenseId
            : this.productLicenseId ?? this.questionResponsesService.getActiveProductLicenseId());
    }

    private normalizeUserId(value: unknown): number | null {
        const numeric = Number(value);
        return Number.isInteger(numeric) && numeric > 0 ? numeric : null;
    }

    private normalizeActivity(dto: QuestionResponseActivityDto): RecentActivityEntry {
        const id = Number(dto.id) || 0;
        const description = String(dto.description || "").trim() || "Activity recorded";
        const createdAt = this.normalizeDate(dto.createdAt ?? dto.created_at) ?? new Date().toISOString();
        const userDisplayName = this.sanitizeDisplayName(
            dto.userDisplayName ?? dto.user_display_name ?? null
        );
        const userRole = this.normalizeRole(dto.userRole ?? dto.user_role ?? null);

        const questionReference = this.normalizeQuestionReference(
            dto.questionReference ?? dto.question_reference ?? null,
            dto.section ?? null,
            dto.target ?? null
        );

        return { id, description, createdAt, userDisplayName, userRole, questionReference };
    }

    private normalizeSelection(sectionId: string | null | undefined, sectionKey: string | null | undefined): NavigationMenuSelection | null {
        const section = typeof sectionId === "string" ? sectionId.trim() : "";
        const target = typeof sectionKey === "string" ? sectionKey.trim() : "";

        if (!section || !target) {
            return null;
        }

        if (!SECTION_PATTERN.test(section)) {
            return null;
        }

        if (!TARGET_PATTERN.test(target)) {
            return null;
        }

        return { section, target };
    }

    private toQuestionReference(selection: NavigationMenuSelection | null): string | null {
        if (!selection) {
            return null;
        }

        return `${selection.section}|${selection.target}`;
    }


    private normalizeQuestionReference(
        reference: string | null | undefined,
        section: string | null | undefined,
        target: string | null | undefined
    ): string | null {
        const parsed = this.parseSelectionFromReference(reference);
        if (parsed) {
            return this.toQuestionReference(parsed);
        }

        const normalized = this.normalizeSelection(section, target);
        return this.toQuestionReference(normalized);
    }

    private formatRequirementReferenceTarget(target: string): string {
        const normalized = target.trim();
        if (!normalized) {
            return "";
        }

        if (normalized.toLowerCase() === "overview") {
            return "Assessment Overview";
        }

        return normalized.replace(/_/g, ".");
    }

    private parseSelectionFromReference(reference: string | null | undefined): NavigationMenuSelection | null {
        if (typeof reference !== "string") {
            return null;
        }

        const [section, target] = reference.split("|");
        if (!section || !target) {
            return null;
        }

        return this.normalizeSelection(section, target);
    }

    private shouldRefreshForMutation(mutation: QuestionResponseMutationResponse | null | undefined): boolean {
        if (!mutation || this.readOnlyUser || !this.canViewRecentActivity) {
            return false;
        }

        const mutationLicenseId = this.normalizeLicenseId(mutation.productLicenseId ?? null);
        const activeLicenseId = this.getCurrentLicenseId();
        if (mutationLicenseId === null || activeLicenseId === null || mutationLicenseId !== activeLicenseId) {
            return false;
        }

        if (this.aggregateByLicense) {
            return true;
        }

        const mutationSelection = mutation.selection
            ? this.normalizeSelection(mutation.selection.section, mutation.selection.target)
            : null;
        const currentSelection = this.currentSelection
            ? this.normalizeSelection(this.currentSelection.section, this.currentSelection.target)
            : null;

        if (!mutationSelection || !currentSelection) {
            return false;
        }

        return (
            mutationSelection.section === currentSelection.section &&
            mutationSelection.target === currentSelection.target
        );
    }

    private isRecentActivityAlreadyCurrent(mutation: QuestionResponseMutationResponse): boolean {
        if (this.pageIndex !== 1) {
            return false;
        }

        const widget = mutation.widgets?.recentActivity;
        if (!widget) {
            return false;
        }

        // Skip redundant reloads when both widget and local state are already empty/current.
        if (this.activities.length === 0 && this.total === 0 && widget.total === 0) {
            return true;
        }

        const latestId = Number(widget.latest?.id ?? 0);
        const firstRowId = Number(this.activities[0]?.id ?? 0);

        if (Number.isInteger(latestId) && latestId > 0 && Number.isInteger(firstRowId) && firstRowId > 0) {
            return widget.total === this.total && latestId === firstRowId;
        }

        const latestCreatedAt = this.normalizeDate(widget.latest?.createdAt ?? null);
        const firstRowCreatedAt = this.normalizeDate(this.activities[0]?.createdAt ?? null);
        if (!latestCreatedAt || !firstRowCreatedAt) {
            return false;
        }

        return widget.total === this.total && latestCreatedAt === firstRowCreatedAt;
    }

    private sanitizeDisplayName(value: string | null | undefined): string | null {
        if (!value) {
            return null;
        }

        const sanitized = String(value)
            .replace(/[\u0000-\u001F\u007F<>]+/g, " ")
            .replace(/\s{2,}/g, " ")
            .trim();

        return sanitized || null;
    }

    private normalizeRole(value: string | null | undefined): string | null {
        if (!value) {
            return null;
        }

        const normalized = String(value).trim();
        return normalized || null;
    }

    private normalizeDate(value: string | Date | null | undefined): string | null {
        if (!value) {
            return null;
        }

        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value.toISOString();
        }

        const trimmed = typeof value === "string" ? value.trim() : "";
        if (!trimmed) {
            return null;
        }

        const parsed = new Date(trimmed);
        return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
    }

    private markForCheck(): void {
        this.cdr.markForCheck();
    }
}
